import React, { FC, useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { CompressionLibraries, handleImageCompression } from 'components/file-upload/compression';
import If from 'components/If/If';
import { FILE_TYPE, FILE_UPLOAD_SIZE } from 'constants/common';
import { NotifierTypes } from 'constants/notifierConstants';
import { showNotifier } from 'containers/app/appSlice';
import { useAppDispatch } from 'hooks';
import { FileRejection, useDropzone } from 'react-dropzone';
import { FileUploadType } from 'containers/disputes/disputes.model';
import { CircleCloseIcon } from 'assets/icons';
import { AttachmentFile } from 'types/baseTypes';
import { UploadImageResponse } from 'containers/jobs/jobs.model';
import { LoadingSpinnerAnimation } from 'assets/animations';
import Lottie from 'lottie-react';

interface MultipleFilesUploadProps {
  className?: string;
  isUploadingImage: boolean;
  isUploadSuccess: boolean;
  setFiles: React.Dispatch<React.SetStateAction<AttachmentFile[]>>;
  id: string;
  height?: string;
  width?: string;
  icon?: string;
  allowMultiple?: boolean;
  subText?: string;
  maxFileSize?: number;
  FileUploadIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string }> | null;
  supportedFileTypes: FileUploadType;
  files: AttachmentFile[];
  maxFileSizeErrorMessage?: string;
  invalideFileTypeErrorMessage?: string;
  allowImageCompression?: boolean;
  onFileRemove: (fileId: number | string) => void;
  onUpload: (file: File) => Promise<UploadImageResponse>;
}

const MultipleFilesUpload: FC<MultipleFilesUploadProps> = ({
  className = '',
  setFiles,
  id,
  height = 'h-[295px]',
  width = 'w-full',
  subText = 'PNG, JPG, or WEBP format files up to 8MB',
  maxFileSize = FILE_UPLOAD_SIZE.SIZE_8_MB_BYTES,
  FileUploadIcon,
  supportedFileTypes,
  files,
  maxFileSizeErrorMessage = 'Image size should not exceed 8 MB',
  invalideFileTypeErrorMessage = 'Invalid file type. Please upload a PNG, JPG, or WEBP format file',
  allowImageCompression = false,
  onFileRemove,
  onUpload,
  isUploadingImage,
  isUploadSuccess,
  allowMultiple = false
}) => {
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const onCompressionInProgress = (progress: number) => {
    if (progress === 100) {
      setImageLoading(false);
    } else {
      setImageLoading(true);
    }
  };

  useEffect(() => {
    if (!isUploadingImage && isUploadSuccess) {
      setImageLoading(false);
    }
  }, [isUploadSuccess, isUploadingImage]);

  const handleDrop = useCallback(async (acceptedFiles, rejectedFiles: FileRejection[]) => {
    setImageLoading(true);
    if (rejectedFiles?.[0]?.file.size > maxFileSize) {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: maxFileSizeErrorMessage,
            secondaryMessage: ''
          },
          type: NotifierTypes.INFO,
          showClose: false,
          fontStyle: 'text-primary font-normal'
        })
      );
      setImageLoading(false);
      return;
    }
    if (rejectedFiles?.[0]?.errors?.[0].code === 'file-invalid-type') {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: invalideFileTypeErrorMessage,
            secondaryMessage: ''
          },
          type: NotifierTypes.INFO,
          showClose: false,
          fontStyle: 'text-primary font-normal'
        })
      );
      setImageLoading(false);
      return;
    }
    const currentFile: File = acceptedFiles[0];
    let compressedFile = currentFile;

    if (currentFile?.size > FILE_UPLOAD_SIZE.SIZE_1_MB && allowImageCompression) {
      if (currentFile?.type === FILE_TYPE.PNG) {
        compressedFile = await handleImageCompression({
          library: CompressionLibraries.COMPRESSOR_JS,
          file: currentFile
        });
      } else {
        compressedFile = await handleImageCompression({
          library: CompressionLibraries.BROWSER_IMAGE_COMPRESSION,
          file: currentFile,
          onCompressionInProgress
        });
      }
    }

    const imageUrlResponse = await onUpload(compressedFile);

    // We use file reader to extract a string out of an image file
    const reader = new FileReader();

    reader.onloadend = () => {
      if (reader.result) {
        setFiles(prev => [
          ...prev,
          {
            tempId: Date.now(),
            title: currentFile.name,
            description: '',
            url: imageUrlResponse.data.imageUrl || imageUrlResponse.data.url,
            urlLink: reader.result as string
          }
        ]);
      }
    };
    reader.readAsDataURL(compressedFile);
  }, []);

  const {
    getRootProps,
    getInputProps,
    open: openFileDialog
  } = useDropzone({
    onDrop: handleDrop,
    accept: {
      // file mime types
      ...supportedFileTypes
    },
    maxSize: maxFileSize,
    multiple: allowMultiple
  });

  return (
    <div className="flex flex-col">
      <div
        {...getRootProps({
          className: twMerge(
            `${width} flex flex-col justify-center relative items-center
           border border-dashed ${height} rounded-md border-border`,
            className
          ),
          onClick: e => e.stopPropagation()
        })}>
        <If condition={!imageLoading}>
          <div className="flex gap-y-3 gap-x-2">
            <div>
              <FileUploadIcon onClick={openFileDialog} className="shrink-0 cursor-pointer" />
            </div>
            <div>
              <div className="mb-1 text-sbase font-semibold text-primaryText">
                <span
                  id={`${id}-upload-a-file`}
                  onClick={openFileDialog}
                  className="cursor-pointer text-sbase font-semibold text-secondary">
                  Upload a file
                </span>{' '}
                or drag and drop
              </div>
              <div className="text-px13 font-semibold text-accentText">{subText}</div>
              <input {...getInputProps()} />
            </div>
          </div>
        </If>
        <If condition={imageLoading}>
          <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
        </If>
      </div>
      <div className="flex flex-col gap-y-2">
        {files.map((file, index) => (
          <div
            key={`note-attachment-${index}`}
            className="flex items-center justify-between rounded border border-borderGray bg-white p-4 pr-3">
            <div className="flex items-center justify-center gap-x-4">
              <img src={file.urlLink} style={{ height: '65px', width: '65px' }} className="rounded-md object-cover" />
              <div className="text-17px font-semibold text-headingGray">{file.title}</div>
            </div>
            <CircleCloseIcon onClick={() => onFileRemove(file.id || file.tempId)} className="shrink-0 cursor-pointer" />
          </div>
        ))}
      </div>
    </div>
  );
};

export default MultipleFilesUpload;
